import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Copy, Archive, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { Modal, inputCls, F, SaveBtn } from "./CrmShared";

const blankStage = () => ({ name: "", color: "#3b82f6", probability: 0, is_won: false, is_lost: false });

function PipelineModal({ open, onClose, pipeline, onSaved }) {
  const [name, setName] = useState("");
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(pipeline?.name || "");
    setStages(pipeline ? pipeline.stages.map((s) => ({ ...s })) : [{ ...blankStage(), name: "New Lead" }, { ...blankStage(), name: "Contacted", color: "#f59e0b", probability: 40 }, { ...blankStage(), name: "Won", color: "#10b981", probability: 100, is_won: true }, { ...blankStage(), name: "Lost", color: "#ef4444", is_lost: true }]);
  }, [open, pipeline]);

  const upd = (i, k, v) => setStages(stages.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const mv = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= stages.length) return;
    const a = [...stages];
    [a[i], a[j]] = [a[j], a[i]];
    setStages(a);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name, stages };
      if (pipeline?.id) await api.put(`/store-admin/crm/pipelines/${pipeline.id}`, body);
      else await api.post("/store-admin/crm/pipelines", body);
      toast.success("Pipeline saved");
      onSaved(); onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={pipeline ? "Edit Pipeline" : "Create Pipeline"} testid="pipeline-modal">
      <form onSubmit={save} className="space-y-4">
        <F label="Pipeline name *"><input required className={inputCls} data-testid="pipeline-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Real Estate Pipeline" /></F>
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground">Stages (in order)</p>
          {stages.map((s, i) => (
            <div key={i} className="rounded-xl border border-border p-2.5 space-y-2" data-testid={`stage-row-${i}`}>
              <div className="flex items-center gap-1.5">
                <input type="color" className="h-8 w-9 rounded-lg border border-input cursor-pointer shrink-0" value={s.color} onChange={(e) => upd(i, "color", e.target.value)} />
                <input className={`${inputCls} flex-1`} data-testid={`stage-name-${i}`} value={s.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Stage name" />
                <button type="button" onClick={() => mv(i, -1)} className="h-8 w-8 rounded-lg hover:bg-muted flex items-center justify-center"><ChevronUp className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => mv(i, 1)} className="h-8 w-8 rounded-lg hover:bg-muted flex items-center justify-center"><ChevronDown className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => setStages(stages.filter((_, j) => j !== i))} className="h-8 w-8 rounded-lg hover:bg-red-50 hover:text-red-600 flex items-center justify-center" data-testid={`stage-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-bold text-muted-foreground flex-wrap">
                <label className="flex items-center gap-1.5">Prob % <input type="number" min="0" max="100" className="h-7 w-16 rounded-lg border border-input px-1.5 text-xs" value={s.probability} onChange={(e) => upd(i, "probability", e.target.value)} /></label>
                <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={s.is_won} onChange={(e) => upd(i, "is_won", e.target.checked)} className="h-3.5 w-3.5 accent-emerald-600" /> Won stage</label>
                <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={s.is_lost} onChange={(e) => upd(i, "is_lost", e.target.checked)} className="h-3.5 w-3.5 accent-red-500" /> Lost stage</label>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setStages([...stages, blankStage()])} data-testid="stage-add"
            className="w-full h-10 rounded-xl border-2 border-dashed border-border text-xs font-extrabold hover:border-foreground/40 flex items-center justify-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Add Stage
          </button>
        </div>
        <SaveBtn saving={saving} label="Save Pipeline" testid="pipeline-save" />
      </form>
    </Modal>
  );
}

export default function CrmPipelines({ refreshKey, onChanged }) {
  const [pipes, setPipes] = useState(null);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/store-admin/crm/pipelines").then((r) => { setPipes(r.data.pipelines); onChanged?.(r.data.pipelines); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const dup = async (p) => { try { await api.post(`/store-admin/crm/pipelines/${p.id}/duplicate`); toast.success("Duplicated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const arch = async (p) => {
    if (!window.confirm(`Archive pipeline "${p.name}"? Leads keep their stage history.`)) return;
    try { await api.delete(`/store-admin/crm/pipelines/${p.id}`); toast.success("Archived"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="crm-pipelines">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-lg">Manage Pipelines</h2>
          <p className="text-xs text-muted-foreground">Create custom sales pipelines with your own stages, colors and probabilities.</p>
        </div>
        <button onClick={() => { setEdit(null); setOpen(true); }} data-testid="pipeline-create"
          className="h-10 px-4 rounded-xl text-white text-xs font-extrabold flex items-center gap-1.5"
          style={{ backgroundImage: "linear-gradient(135deg, #059669, #0d9488)" }}><Plus className="h-3.5 w-3.5" /> Create Pipeline</button>
      </div>
      {pipes === null ? <div className="h-28 flex items-center justify-center"><div className="h-7 w-7 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>
        : (
          <div className="space-y-3">
            {pipes.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border bg-background p-4" data-testid={`pipeline-card-${p.id}`}>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-bold text-sm truncate">{p.name}</p>
                    {p.is_default && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-extrabold uppercase">Default</span>}
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { setEdit(p); setOpen(true); }} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted" title="Edit" data-testid={`pipeline-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => dup(p)} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted" title="Duplicate" data-testid={`pipeline-dup-${p.id}`}><Copy className="h-3.5 w-3.5" /></button>
                    {!p.is_default && <button onClick={() => arch(p)} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-amber-50 hover:text-amber-600" title="Archive" data-testid={`pipeline-arch-${p.id}`}><Archive className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {p.stages.map((s) => (
                    <span key={s.key} className="px-2.5 py-1 rounded-full text-[10px] font-extrabold text-white" style={{ backgroundColor: s.color }}>{s.name}{s.is_won ? " ✓" : s.is_lost ? " ✕" : ""}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      <PipelineModal open={open} onClose={() => setOpen(false)} pipeline={edit} onSaved={load} />
    </div>
  );
}
